import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, X, Image, Camera, Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";

interface ImageUploaderProps {
  currentImageUrl: string | null;
  title: string;
  onImageSelected: (file: File) => Promise<void>;
  onRemoveImage?: () => void;
  aspectRatio?: "square" | "banner";
  className?: string;
}

export const ImageUploader = ({
  currentImageUrl,
  title,
  onImageSelected,
  onRemoveImage,
  aspectRatio = "square",
  className = "",
}: ImageUploaderProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [tempObjectUrl, setTempObjectUrl] = useState<string | null>(null);
  const { toast } = useToast();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  // Atualiza o preview quando currentImageUrl muda
  useEffect(() => {
    if (currentImageUrl !== previewUrl && !isUploading) {
      // Se temos uma URL temporária e agora recebemos a URL final, limpa a temporária
      if (tempObjectUrl && currentImageUrl) {
        URL.revokeObjectURL(tempObjectUrl);
        setTempObjectUrl(null);
      }
      setPreviewUrl(currentImageUrl);
    }
  }, [currentImageUrl, previewUrl, isUploading, tempObjectUrl]);

  // Cleanup da URL temporária quando o componente é desmontado
  useEffect(() => {
    return () => {
      if (tempObjectUrl) {
        URL.revokeObjectURL(tempObjectUrl);
      }
    };
  }, [tempObjectUrl]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log(`📱 [ImageUploader] Mobile: ${isMobile}, File selected:`, {
      name: file.name,
      size: file.size,
      type: file.type
    });

    // Validação do tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Tipo de arquivo inválido",
        description: "Por favor, selecione apenas arquivos de imagem (JPG, PNG, GIF, WebP).",
        variant: "destructive"
      });
      // Limpa o input
      e.target.value = '';
      return;
    }

    // Validação do tamanho (5MB máximo)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Arquivo muito grande",
        description: "O tamanho máximo permitido é 5MB.",
        variant: "destructive"
      });
      // Limpa o input
      e.target.value = '';
      return;
    }

    try {
      setIsUploading(true);
      
      // Limpa URL temporária anterior se existir
      if (tempObjectUrl) {
        URL.revokeObjectURL(tempObjectUrl);
      }
      
      // Cria URL para preview temporário
      const objectUrl = URL.createObjectURL(file);
      setTempObjectUrl(objectUrl);
      setPreviewUrl(objectUrl);
      
      console.log(`📤 [ImageUploader] Iniciando upload...`);
      
      // Chama a função de upload e aguarda o resultado
      await onImageSelected(file);
      
      console.log(`✅ [ImageUploader] Upload concluído com sucesso`);
      
      // Não limpa a URL temporária aqui - será limpa quando currentImageUrl for atualizado
      
    } catch (error) {
      console.error(`❌ [ImageUploader] Erro no upload:`, error);
      
      // Reverte o preview em caso de erro
      if (tempObjectUrl) {
        URL.revokeObjectURL(tempObjectUrl);
        setTempObjectUrl(null);
      }
      setPreviewUrl(currentImageUrl);
      
      // Mostra toast de erro se não foi mostrado pelo hook
      toast({
        title: "Erro no upload",
        description: "Não foi possível enviar a imagem. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
      // Limpa o input para permitir selecionar o mesmo arquivo novamente se necessário
      e.target.value = '';
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    // Mesma validação de tipo e tamanho
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Tipo de arquivo inválido",
        description: "Por favor, selecione apenas arquivos de imagem (JPG, PNG, GIF, WebP).",
        variant: "destructive"
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Arquivo muito grande",
        description: "O tamanho máximo permitido é 5MB.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsUploading(true);
      
      // Limpa URL temporária anterior se existir
      if (tempObjectUrl) {
        URL.revokeObjectURL(tempObjectUrl);
      }
      
      const objectUrl = URL.createObjectURL(file);
      setTempObjectUrl(objectUrl);
      setPreviewUrl(objectUrl);
      
      // Chama a função de upload e aguarda o resultado
      await onImageSelected(file);
      
      // Não limpa a URL temporária aqui - será limpa quando currentImageUrl for atualizado
    } catch (error) {
      console.error(`❌ [ImageUploader] Erro no drop:`, error);
      
      // Reverte o preview em caso de erro
      if (tempObjectUrl) {
        URL.revokeObjectURL(tempObjectUrl);
        setTempObjectUrl(null);
      }
      setPreviewUrl(currentImageUrl);
      
      toast({
        title: "Erro no upload",
        description: "Não foi possível enviar a imagem. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleButtonClick = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    // Em mobile, garantir que o input seja clicado corretamente
    if (fileInputRef.current) {
      // Força o foco e clica no input
      fileInputRef.current.focus();
      fileInputRef.current.click();
    }
  };

  const handleRemove = () => {
    // Limpa URL temporária se existir
    if (tempObjectUrl) {
      URL.revokeObjectURL(tempObjectUrl);
      setTempObjectUrl(null);
    }
    setPreviewUrl(null);
    if (onRemoveImage) {
      onRemoveImage();
    }
  };

  // Instruções baseadas no tipo de imagem
  const getInstructions = () => {
    if (aspectRatio === 'banner') {
      return {
        recommended: "Recomendado: 1200x400px (proporção 3:1)",
        formats: "Formatos: JPG, PNG, GIF, WebP",
        maxSize: "Tamanho máximo: 5MB",
        tips: "Dica: Use imagens horizontais para melhor resultado"
      };
    } else {
      return {
        recommended: "Recomendado: 400x400px (quadrada)",
        formats: "Formatos: JPG, PNG, GIF, WebP", 
        maxSize: "Tamanho máximo: 5MB",
        tips: "Dica: Use imagens quadradas para melhor resultado"
      };
    }
  };

  const instructions = getInstructions();

  return (
    <Card className={`overflow-hidden ${className}`}>
      <CardContent 
        className={`p-0 ${isDragging ? 'border-2 border-dashed border-blue-400' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center relative w-full">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            multiple={false}
            id={`file-input-${title.replace(/\s+/g, '-').toLowerCase()}`}
            style={{ 
              position: 'absolute',
              width: '100%',
              height: '100%',
              opacity: 0,
              cursor: 'pointer',
              zIndex: 10,
              top: 0,
              left: 0,
              fontSize: '16px' // Previne zoom no iOS
            }}
          />
          
          {previewUrl ? (
            <div className="relative w-full">
              <img 
                src={previewUrl} 
                alt={title}
                className={`w-full image-preview ${aspectRatio === 'square' ? 'aspect-square object-cover' : 'aspect-[3/1] object-cover'}`}
              />
              {!isUploading && (
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-8 w-8 rounded-full opacity-80 hover:opacity-100"
                  onClick={handleRemove}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
              {isUploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                    <div className="flex items-center gap-2 text-white text-sm">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Enviando...
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <label
              htmlFor={`file-input-${title.replace(/\s+/g, '-').toLowerCase()}`}
              className={`flex flex-col items-center justify-center p-6 bg-gray-800 text-gray-300 cursor-pointer hover:bg-gray-700 active:bg-gray-600 transition-colors upload-area ${
                aspectRatio === 'square' ? 'aspect-square w-full' : 'aspect-[3/1] w-full'
              }`}
              style={{
                position: 'relative',
                zIndex: 1
              }}
            >
              {isMobile ? (
                <Camera className="h-12 w-12 mb-2 opacity-50" />
              ) : (
                <Image className="h-12 w-12 mb-2 opacity-50" />
              )}
              <p className="text-sm text-center font-medium">{title}</p>
              <Button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleButtonClick(e);
                }} 
                variant="outline" 
                className="mt-4 flex items-center gap-2 upload-button touch-manipulation"
                disabled={isUploading}
                style={{ 
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation',
                  position: 'relative',
                  zIndex: 2
                }}
              >
                {isMobile ? (
                  <>
                    <Upload className="h-4 w-4" />
                    <span>Selecionar Foto</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    <span>Escolher Arquivo</span>
                  </>
                )}
              </Button>
              {isMobile && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Escolha entre câmera ou galeria
                </p>
              )}
            </label>
          )}
          
          {/* Instruções detalhadas */}
          <div className="w-full p-4 bg-gray-900/50 border-t border-gray-700">
            <div className="flex items-start gap-2 mb-2">
              <Info className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-gray-400 space-y-1">
                <p className="font-medium text-blue-400">{instructions.recommended}</p>
                <p>{instructions.formats}</p>
                <p>{instructions.maxSize}</p>
                <p className="text-gray-500 italic">{instructions.tips}</p>
              </div>
            </div>
            {!isMobile && (
              <p className="text-xs text-gray-500 mt-2">
                💡 Você também pode arrastar e soltar a imagem aqui
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
